/* eslint-disable*/
import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import { useNavigate } from "react-router-dom";

// Import the lightbox component and styles
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const DailyShareviewResult = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [shareLoading, setShareLoading] = useState(null);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");
  const [shareViewData, setShareViewData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const navigate = useNavigate();

  // New date range states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filtersReset, setFiltersReset] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Format date for API request (YYYY‑MM‑DD)
  const formatAPIDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchShareViewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}researchCalls/dailyshareviewresult`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            limit,
            page,
            ...(startDate &&
              endDate && {
              sdate: formatAPIDate(startDate),
              edate: formatAPIDate(endDate),
            }),
          },
        }
      );

      const { shareview, totalPages, total } = response.data.data;
      setShareViewData(shareview);
      setPageCount(totalPages);
      setTotal(total);
    } catch (error) {
      setError(error.message);
      showToast.error("Failed to fetch share view data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShareViewData();
  }, [page]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchShareViewData();
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setFiltersReset(true);
  };

  useEffect(() => {
    if (filtersReset) {
      fetchShareViewData();
      setFiltersReset(false);
    }
  }, [filtersReset]);

  const handleChange = (dates) => {
    if (!dates) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  // Function to open lightbox for a specific image
  const openLightbox = (imgUrl) => {
    if (!imgUrl) {
      showToast.error("No image available to view");
      return;
    }

    const index = shareViewData.findIndex((post) => post.imgurl === imgUrl);
    if (index >= 0) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const handleDownload = (s3Url, filename, id) => {
    if (!s3Url) {
      showToast.error("No image available for download");
      return;
    }

    try {
      setDownloadLoading(id);

      const link = document.createElement("a");
      link.href = s3Url;
      link.setAttribute("download", filename || "research-image.png");
      link.setAttribute("target", "_blank"); // fallback for some browsers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast.success("Download started");
    } catch (err) {
      console.error("Download failed", err);
      showToast.error("Failed to download image");
    } finally {
      setDownloadLoading(null);
    }
  };


  const handleShare = async (id) => {
    try {
      setLoading(true);
      setShareLoading(id);
      const response = await axios.post(
        `${API_URL}notification/${id}/share`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success || response.status === 201) {
        showToast.success("Shared successfully");
        navigate("/research/performance/shareview");
      } else {
        showToast.error(response.data.message || "Failed to share");
      }
    } catch (err) {
      console.error("Share failed", err);
      showToast.error(err.response?.data?.message || "Failed to share");
    } finally {
      setLoading(false);
      setShareLoading(null);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  if (loading && !downloadLoading && !shareLoading) {
    return <LoadingSpinner />;
  }

  // Prepare slides for lightbox - only include posts that have images
  const slides = shareViewData
    .filter((post) => post.imgurl)
    .map((post) => ({ src: post.imgurl }));

  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      {/* Search / Filter Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <form onSubmit={handleSubmit}>
          <div className="panel-heading border-0">
            <span className="panel-title">Search</span>
          </div>
          <div className="panel-body">
            <div className="d-flex align-items-center">
              <DatePicker
                selected={startDate}
                onChange={handleChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                isClearable
                toggleCalendarOnIconClick
                placeholderText="Select a date range"
                className="form-control"
                monthsShown={3}
                openToDate={startDate}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={15}
                minDate={new Date(2018, 0, 1)}
                maxDate={new Date(2030, 11, 31)}
                dateFormat="dd-MM-yyyy"
              />

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary mx-2">
                  Submit
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleClear}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Sharing Research Calls</h4>
          </div>
          <div className="me-3 ">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {shareViewData.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-condensed table-bordered bg-white">
              <thead>
                <tr>
                  <th className="text-center">Sr. No.</th>
                  <th>Image</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shareViewData.map((post, index) => {
                  const credate = new Date(post.created_on).toLocaleString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }
                  );

                  return (
                    <tr key={post.id}>
                      <td className="text-center">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>
                        {post.imgurl ? (
                          <img
                            src={post.imgurl}
                            alt=""
                            style={{ width: "200px", cursor: "pointer" }}
                            onClick={() => openLightbox(post.imgurl)}
                          />
                        ) : (
                          <span className="text-muted">No image available</span>
                        )}
                      </td>
                      <td>{credate}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* View Button - Now opens lightbox instead of new tab */}
                          <button
                            className="btn btn-warning"
                            title="View"
                            disabled={!post.imgurl}
                            onClick={() => openLightbox(post.imgurl)}
                          >
                            <FontAwesomeIcon icon={faSearch} />
                          </button>

                          <button
                            className="btn btn-danger"
                            onClick={() =>
                              handleDownload(
                                post.imgurl,
                                `research-${post.id}.png`,
                                post.id
                              )
                            }
                            disabled={!post.imgurl || downloadLoading === post.id}
                          >
                            {downloadLoading === post.id ? (
                              <div className="spinner-border spinner-border-sm" />
                            ) : (
                              <FontAwesomeIcon icon={faDownload} />
                            )}
                          </button>

                          <button
                            className="btn btn-primary"
                            onClick={() => handleShare(post.id)}
                            disabled={shareLoading === post.id}
                            title="Share"
                          >
                            {shareLoading === post.id ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              <FontAwesomeIcon icon={faShare} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-3 text-center">
            No records found.{" "}
            <a href="/research/performance/edit/">Add a record</a>.
          </div>
        )}
      </div>

      {/* Lightbox component */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={slides}
        index={lightboxIndex}
        on={{
          view: ({ index: currentIndex }) => setLightboxIndex(currentIndex),
        }}
      />

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default DailyShareviewResult;
